import { useQuery } from '@apollo/client'
import { ExpandLess } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Typography } from '@mui/material'
import {
    DataGridPro,
    DataGridProProps,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GridColDef,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { GetOtherSearchesForCandidateQuery } from '__generated__/graphql'
import { GetOtherSearchesForCandidate } from 'client/graphql/search.gql'
import React from 'react'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import { CenteredBox } from 'shared/components/CenteredBox'
import OtherSearchesContent from './OtherSearchesContent'

export interface OtherSearchesForCandidateProps {
    candidateId: string
}

export type OtherSearchesTableModel =
    GetOtherSearchesForCandidateQuery['getCandidateInOtherSearchesForClient'][number]

const OtherSearchesForCandidate: React.FC<OtherSearchesForCandidateProps> = ({
    candidateId,
}) => {
    const gridApi = useGridApiRef()
    const { data, loading } = useQuery(GetOtherSearchesForCandidate, {
        variables: { candidateId },
        skip: !candidateId,
        onError: (error) => {
            console.error('Error fetching other searches for candidate', error)
        },
    })
    const getDetailPanelContent: DataGridProProps<OtherSearchesTableModel>['getDetailPanelContent'] =
        React.useCallback(
            (params) => (
                <OtherSearchesContent
                    rosterName={params.row.roster?.rosterName}
                    candidateProcessStatusHistory={
                        params.row.candidateProcessStatusHistory
                    }
                />
            ),
            []
        )

    const getDetailPanelHeight: DataGridProProps<OtherSearchesTableModel>['getDetailPanelHeight'] =
        React.useCallback(() => 'auto', [])
    return (
        <DataGridPro
            apiRef={gridApi}
            columns={columns}
            scrollbarSize={0}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableColumnSorting
            disableMultipleColumnsSorting
            disableRowSelectionOnClick
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            hideFooter
            loading={loading}
            getRowId={(row) => row.id}
            rows={data?.getCandidateInOtherSearchesForClient ?? []}
            slots={{
                noRowsOverlay: getNoRowsOverlay,
                detailPanelExpandIcon: ExpandMoreIcon,
                detailPanelCollapseIcon: ExpandLess,
            }}
            sortingMode="server"
            sx={{
                '& .MuiDataGrid-columnHeader': {
                    cursor: 'default', // Remove pointer cursor from column headers
                },
            }}
        />
    )
}

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No Searches Found</Typography>
    </CenteredBox>
)

const columns: GridColDef<OtherSearchesTableModel>[] = [
    {
        field: 'searchName',
        headerName: 'Search Name',
        flex: 2,
        renderCell: ({ row }) => (
            <Box
                alignContent={'center'}
                display="flex"
                justifyItems={'center'}
                alignItems={'center'}
                height={'100%'}
            >
                <Typography
                    variant="body1"
                    flexWrap={'wrap'}
                    overflow="hidden"
                    textOverflow="ellipsis"
                >
                    {`${row.roster?.rosterName || 'Unknown'}`}
                </Typography>
            </Box>
        ),
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: ({ row }) => (
            <ProcessStatusPill
                status={row.candidateProcessStatus?.processStatus?.status}
                statusLabel={
                    row.candidateProcessStatus?.processStatus?.statusLabel
                }
                title={
                    row.candidateProcessStatus?.processStatus
                        ?.processStatusSummaryGroup?.label
                }
                summaryGroup={
                    row.candidateProcessStatus?.processStatus
                        ?.processStatusSummaryGroup?.summaryGroup
                }
                summaryGroupLabel={
                    row.candidateProcessStatus?.processStatus
                        ?.processStatusSummaryGroup?.label
                }
            />
        ),
    },
    {
        headerClassName: 'no-separator',
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    },
]

export default OtherSearchesForCandidate
